import { Injectable, inject } from '@angular/core';
import { CoreUtilsService } from '@bepatient/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Platform } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { EDocsApiService } from './edocs-api.service';
import {
  EDocsActionDeleteFile,
  EDocsActionDeleteFileFailed,
  EDocsActionDeleteFileSuccess,
  EDocsActionDeleteFolder,
  EDocsActionDeleteFolderFailed,
  EDocsActionDeleteFolderSuccess,
  EDocsActionDownloadFile,
  EDocsActionDownloadFileFailed,
  EDocsActionDownloadFileSuccess,
  EDocsActionLoadEDocsFailed,
  EDocsActionLoadEDocsSuccess,
  EDocsActions,
  EDocsActionSaveFile,
  EDocsActionSaveFileFailed,
  EDocsActionSaveFileSuccess,
  EDocsActionSaveFolder,
  EDocsActionSaveFolderFailed,
  EDocsActionSaveFolderSuccess,
  EDocsActionTypes,
  EDocsActionUpdateFile,
  EDocsActionUpdateFileFailed,
  EDocsActionUpdateFileSuccess,
  EDocsActionUpdateFolder,
  EDocsActionUpdateFolderFailed,
  EDocsActionUpdateFolderSuccess
} from './edocs.actions';

@Injectable()
export class EDocsEffects {
  private actions$ = inject(Actions);
  private platform = inject(Platform);
  private eDocsApiService = inject(EDocsApiService);
  private utils = inject(CoreUtilsService);

  loadEDocs$: Observable<EDocsActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(EDocsActionTypes.LOAD_EDOCS),
      switchMap(() =>
        this.eDocsApiService.loadEDocs().pipe(
          map(res => new EDocsActionLoadEDocsSuccess(res)),
          catchError(() => of(new EDocsActionLoadEDocsFailed()))
        )
      )
    )
  );

  saveFile$: Observable<EDocsActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(EDocsActionTypes.SAVE_FILE),
      map((action: EDocsActionSaveFile) => action.payload),
      switchMap(payload =>
        this.eDocsApiService
          .saveFile(payload.file, payload.content, payload.fileName)
          .pipe(
            map(() => new EDocsActionSaveFileSuccess(payload.file)),
            catchError(() => of(new EDocsActionSaveFileFailed()))
          )
      )
    )
  );

  saveFolder$: Observable<EDocsActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(EDocsActionTypes.SAVE_FOLDER),
      map((action: EDocsActionSaveFolder) => action.payload),
      switchMap(folder =>
        this.eDocsApiService.saveFolder(folder).pipe(
          map(() => new EDocsActionSaveFolderSuccess(folder)),
          catchError(() => of(new EDocsActionSaveFolderFailed()))
        )
      )
    )
  );

  updateFile$: Observable<EDocsActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(EDocsActionTypes.UPDATE_FILE),
      map((action: EDocsActionUpdateFile) => action.payload),
      switchMap(file =>
        this.eDocsApiService.updateFile(file).pipe(
          map(() => new EDocsActionUpdateFileSuccess(file)),
          catchError(() => of(new EDocsActionUpdateFileFailed()))
        )
      )
    )
  );

  updateFolder$: Observable<EDocsActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(EDocsActionTypes.UPDATE_FOLDER),
      map((action: EDocsActionUpdateFolder) => action.payload),
      switchMap(folder =>
        this.eDocsApiService.updateFolder(folder).pipe(
          map(() => new EDocsActionUpdateFolderSuccess(folder)),
          catchError(() => of(new EDocsActionUpdateFolderFailed()))
        )
      )
    )
  );

  deleteFile$: Observable<EDocsActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(EDocsActionTypes.DELETE_FILE),
      map((action: EDocsActionDeleteFile) => action.payload),
      switchMap(file =>
        this.eDocsApiService.deleteFile(file).pipe(
          map(() => new EDocsActionDeleteFileSuccess(file)),
          catchError(() => of(new EDocsActionDeleteFileFailed()))
        )
      )
    )
  );

  deleteFolder$: Observable<EDocsActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(EDocsActionTypes.DELETE_FOLDER),
      map((action: EDocsActionDeleteFolder) => action.payload),
      switchMap(folder =>
        this.eDocsApiService.deleteFolder(folder).pipe(
          map(() => new EDocsActionDeleteFolderSuccess(folder)),
          catchError(() => of(new EDocsActionDeleteFolderFailed()))
        )
      )
    )
  );

  downloadFile$: Observable<EDocsActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(EDocsActionTypes.DOWNLOAD_FILE),
      map((action: EDocsActionDownloadFile) => action.payload),
      switchMap(file => {
        let writeDirectory = this.platform.is('android')
          ? Directory.Documents // this.file.externalDataDirectory
          : Directory.Documents;
        let writeDirectoryPath = this.platform.is('android')
          ? `bResearch/`
          : `/`;

        return this.eDocsApiService.getDownloadFile(file).pipe(
          switchMap(data => {
            // file name to save in user device
            const fileName = data.payload.file_name;
            const { fileMimeType, fileExtension } =
              this.utils.getFileMimeTypeAndExt(fileName);
            const result = new Subject<boolean>();
            if (this.platform.is('ios') || this.platform.is('android')) {
              return from(
                Filesystem.writeFile({
                  path: writeDirectoryPath + fileName,
                  data: data.payload.content,
                  directory: writeDirectory,
                  recursive: true
                })
              );
            } else {
              try {
                const objBlob = this.utils.convertBase64ToBlob(
                  data.payload.content,
                  fileMimeType
                );
                const dataObjUrl = window.URL.createObjectURL(objBlob);
                const link = document.createElement('a');
                link.href = dataObjUrl;
                link.download = fileName;

                // this is necessary as link.click() does not work on the latest firefox
                link.dispatchEvent(
                  new MouseEvent('click', {
                    bubbles: true,
                    cancelable: true,
                    view: window
                  })
                );

                setTimeout(() => {
                  // For Firefox it is necessary to delay revoking the ObjectURL
                  window.URL.revokeObjectURL(dataObjUrl);
                  link.remove();
                  result.next(true);
                  result.complete();
                }, 100);
              } catch (e) {
                result.error(e);
                result.complete();
              }
              return result.asObservable();
            }
          }),
          map(fun => {
            return new EDocsActionDownloadFileSuccess(file);
          }),
          catchError(e => {
            return of(new EDocsActionDownloadFileFailed());
          })
        );
      })
    )
  );
}
